import CryptoJS from "crypto-js";
import envConfig from "../config";
import {
  getCookie,
  setCrossDomainCookie,
  clearAllCookies,
  clearSessionStorage,
} from "./cookie-utils";
import { mergeOlivsAuthIntoHeaders } from "./olivs-auth-headers";
import {
  authxSessionHeaders,
  buildAuthxSessionGetUrl,
} from "./authx-login-helpers";
import {
  isAuthxAppsExplicitFailureBody,
  isAuthxAppsOperationSuccess,
  parseAuthxAppsResponseBody,
} from "./authx-apps-response";

export { getCookie } from "./cookie-utils";

/** Plain password → lowercase SHA-256 hex (AuthX loginHash / LoginSecured). */
async function sha256Hex(plainText) {
  const data = new TextEncoder().encode(plainText);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

export const getLoginSecured = async (login, password) => {
  if (!login && !password && getCookie("loginSecuredHash")) {
    return getCookie("loginSecuredHash");
  }
  const loginEmail = login || getCookie("loginEmail");
  const loginPassword = password;
  const passwordFromCookies = getCookie("loginPassword");
  let hashedPassword;
  if (loginPassword) {
    hashedPassword = await sha256Hex(loginPassword);
  } else {
    hashedPassword = passwordFromCookies;
  }
  if (!hashedPassword) {
    hashedPassword = passwordFromCookies;
  }
  const combinedString = `${loginEmail}:${hashedPassword}`;
  if (combinedString === "undefined:undefined") {
    return undefined;
  }
  const base64Encoded = btoa(combinedString);
  setCrossDomainCookie("loginSecuredHash", base64Encoded);

  return base64Encoded;
};

export const getSHA256Hash = (password) => {
  if (!password) {
    return null;
  }
  return CryptoJS.SHA256(password).toString();
};

export const getHeaders = (dbId) =>
  mergeOlivsAuthIntoHeaders(
    {
      "Content-Type": "application/json",
      Accept: "application/json",
      "olivs-api-real-ip": "31.42.0.186",
      "olivs-api-computer-name": "BTM-WS2019",
    },
    dbId ?? getCookie("databaseId"),
  );

/** Bootstrap requests before session exists (no access/refresh keys). */
export const default_headers = {
  Accept: "application/json",
  "Content-Type": "application/json",
  "olivs-api-real-ip": "31.42.0.186",
  "olivs-api-computer-name": "BTM-WS2019",
  "database-id": "0",
};

export const LOGIN_API_OLIVS_APP_URL = `https://${envConfig.apiLogin}/api/v1/`;

export const clearLocalSession = () => {
  clearAllCookies();
  clearSessionStorage();
  try {
    localStorage.removeItem("olivs_session_backup");
  } catch {
    // Cookie cleanup remains effective when storage is unavailable.
  }
};

/** OLIVS v3/v5 JSON success (SuccessYN, typo SuccesYN, or ResultCode OK). */
export function isOlivsApiSuccess(json, httpOk = true) {
  if (!httpOk) return false;
  if (!json || typeof json !== "object") return httpOk;
  if (json.SuccessYN === "Y" || json.SuccesYN === "Y") return true;
  const rc = String(json.ResultCode ?? json.resultCode ?? "")
    .trim()
    .toUpperCase();
  return rc === "OK";
}

export const logoutUser = async (_dbId) => {
  const accessKey = getCookie("accessKey");
  const refreshKey = getCookie("refreshKey");
  try {
    if (
      !accessKey ||
      accessKey === "undefined" ||
      !refreshKey ||
      refreshKey === "undefined"
    ) {
      return;
    }

    const response = await fetch(
      buildAuthxSessionGetUrl("logout", accessKey, refreshKey),
      {
        method: "GET",
        headers: authxSessionHeaders(accessKey, refreshKey),
      },
    );

    const data = await parseAuthxAppsResponseBody(response);

    if (data && isAuthxAppsExplicitFailureBody(data)) {
      console.error("logoutUser: Server logout failed:", data);
    }
  } catch (error) {
    console.error("logoutUser: Error during server logout:", error);
  } finally {
    clearLocalSession();
  }
};

export const logoutEverywhere = async () => {
  const accessKey = getCookie("accessKey");
  const refreshKey = getCookie("refreshKey");

  try {
    if (
      !accessKey ||
      accessKey === "undefined" ||
      !refreshKey ||
      refreshKey === "undefined"
    ) {
      return false;
    }

    const response = await fetch(
      buildAuthxSessionGetUrl("logout-from-everywhere", accessKey, refreshKey),
      {
        method: "GET",
        headers: authxSessionHeaders(accessKey, refreshKey),
      },
    );
    const data = await parseAuthxAppsResponseBody(response);
    return isAuthxAppsOperationSuccess(response.ok, data);
  } finally {
    clearLocalSession();
  }
};
